import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { HotKeys } from 'react-hotkeys';

import {
  getInchMeasurements,
  getCardElementFrameStyle,
  getCardElementFrameContainerStyle,
} from './helpers';
import CardDesignerCardElementResizeControls from '../CardDesignerCardElementControls/components/CardDesignerCardElementResizeControls';
import CardDesignerCardElementDragControls from '../CardDesignerCardElementControls/components/CardDesignerCardElementDragControls';
import CardDesignerCardElementRotateControls from '../CardDesignerCardElementControls/components/CardDesignerCardElementRotateControls';
import useComponentBounds from '../../../../../../hooks/useComponentBounds';
import {
  CARD_ELEMENT_TYPE,
  ORIENTATION,
} from '../../../../../../services/cards/constants';

const propTypes = {
  className: PropTypes.string,
  element: PropTypes.shape({
    cardElement: PropTypes.object.isRequired,
    cardElementConfiguration: PropTypes.object.isRequired,
    side: PropTypes.string.isRequired,
  }),
  cardBounds: PropTypes.shape({
    width: PropTypes.number,
  }),
  cardPrintSize: PropTypes.shape({
    id: PropTypes.string,
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  cardOrientation: PropTypes.oneOf(Object.values(ORIENTATION)),
  resizable: PropTypes.bool,
  draggable: PropTypes.bool,
  rotable: PropTypes.bool,
  isSelected: PropTypes.bool,
  onCardElementUpdate: PropTypes.func.isRequired,
  onCardElementDelete: PropTypes.func,
};

const CardDesignerCardElementFrame = ({
  className,
  element,
  cardBounds,
  cardPrintSize,
  cardOrientation,
  children,
  resizable,
  draggable,
  rotable,
  isSelected,
  onCardElementUpdate,
  onCardElementDelete,
}) => {
  const [localElement, setLocalElement] = useState({
    ...element,
    cardElement: {
      ...element.cardElement,
      frame: { ...(element.cardElement.frame ?? {}) },
    },
  });
  const ref = useRef();
  const { bounds, recalculateBounds } = useComponentBounds(ref);
  const { cardElement } = element;
  const { frame } = cardElement ?? {};
  const classes = classNames({
    [className]: !!className,
    'is-selected': isSelected,
    'card-designer-card-element-frame': true,
    [`card-element-frame-${element.cardElement.type.toLowerCase()}`]: true,
  });

  // For the local element, we only care for the element's
  // cardElement & frame, so need to deep copy the rest
  useEffect(() => {
    setLocalElement({
      ...element,
      cardElement: {
        ...element.cardElement,
        frame: { ...(element.cardElement.frame ?? {}) },
      },
    });
    // We must force to recalculate bounds since things may have been dragged
    // which does *not* actually generate new bounds. Forcing recalculation
    // does allow us to properly resize from all 4 sides.
    recalculateBounds();
  }, [element]);

  const inchMeasurements = getInchMeasurements(
    frame,
    cardPrintSize,
    cardOrientation,
  );

  const keyMap = {
    DELETE: ['del', 'delete'],
  };
  const keyHandlers = {
    DELETE: () => {
      onCardElementDelete?.(element.cardElement.id);
    },
  };

  return (
    <HotKeys keyMap={keyMap} handlers={keyHandlers}>
      <div
        ref={ref}
        className={classes}
        style={getCardElementFrameStyle(localElement)}
      >
        <div
          className="card-designer-card-element-frame-container"
          style={getCardElementFrameContainerStyle(localElement, bounds)}
        >
          {children}
        </div>
        {isSelected && draggable && (
          <CardDesignerCardElementDragControls
            element={element}
            localElement={localElement}
            setLocalElement={setLocalElement}
            cardElementBounds={bounds}
            onCardElementUpdate={onCardElementUpdate}
          />
        )}
        {isSelected && resizable && (
          <CardDesignerCardElementResizeControls
            element={element}
            localElement={localElement}
            setLocalElement={setLocalElement}
            cardElementBounds={bounds}
            cardBounds={cardBounds}
            onCardElementUpdate={onCardElementUpdate}
          />
        )}
        {isSelected && rotable && (
          <CardDesignerCardElementRotateControls
            element={element}
            localElement={localElement}
            setLocalElement={setLocalElement}
            cardElementBounds={bounds}
            cardBounds={cardBounds}
            onCardElementUpdate={onCardElementUpdate}
          />
        )}
        {element.cardElement.type === CARD_ELEMENT_TYPE.PHOTO &&
          inchMeasurements.width && (
            <div className="card-designer-element-measurements">
              {inchMeasurements.width} &times; {inchMeasurements.height}in
            </div>
          )}
      </div>
    </HotKeys>
  );
};

CardDesignerCardElementFrame.propTypes = propTypes;

export default CardDesignerCardElementFrame;
